import { cn } from 'utils/className';
import { spawn } from 'utils/translations';
import { FEATURE_KEYS } from '../consts';

const spawnPopup = spawn('billing.popup');

interface FeatureListProps {
  /** i18n key suffixes for the features to render, in display order. */
  featureKeys?: readonly string[];
  /** i18n namespace under `billing.popup` for each feature item (default: `feature`). */
  featureNamespace?: string;
  /** Optional lead-in label shown before the items (default: the "What's included:" header). */
  intro?: string;
  /** Optional className for the features container (default: `grid auto-cols-max grid-rows-2 grid-flow-col gap-x-6 gap-y-2`). */
  featuresClassName?: string;
  /** Optional className for the root container. */
  propsClassName?: string;
}

/**
 * "What's included" row. Defaults to the Team Pro feature checklist; pass
 * `featureKeys` / `featureNamespace` / `intro` to render a tier-specific list
 * (e.g. the Enterprise "Everything in Team Pro, plus:" list).
 */
export const FeatureList = ({
  featureKeys = FEATURE_KEYS,
  featureNamespace = 'feature',
  intro,
  featuresClassName,
  propsClassName,
}: FeatureListProps = {}) => {
  const introLabel = intro ?? spawnPopup('whats-included');
  const className = cn('flex gap-x-5 gap-y-2 items-start', propsClassName);

  return (
    <div className={className}>
      <div className="text-sm text-text-general-neutral-mid whitespace-nowrap">
        {introLabel}
      </div>
      <div
        className={cn(
          'grid auto-cols-max grid-rows-2 grid-flow-col gap-x-6 gap-y-2',
          featuresClassName,
        )}
      >
        {featureKeys.map((key) => (
          <span
            key={key}
            className="text-xs text-[var(--color-purple-muted-350)] whitespace-nowrap"
          >
            ✓&nbsp;&nbsp;{spawnPopup(`${featureNamespace}.${key}`)}
          </span>
        ))}
      </div>
    </div>
  );
};
